import { useState, useEffect, useRef } from "react";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  getTemplateLibrary,
  cloneTemplate,
  createTemplateLibrary,
  updateTemplateLibrary,
  deleteTemplateLibrary,
  syncInteraktTemplates,
} from "../api/TemplateLibraryApi";
import { getMedia } from "../api/MediaApi";
import { getDepartments } from "../api/HumanRiskScorecardApi";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Search, Library, Mail, MessageSquare, Phone, Copy, Eye, Filter,
  Plus, Edit, Trash2, Loader2, Check, ChevronsUpDown, ChevronLeft, ChevronRight,
  Image as ImageIcon, Paperclip, X, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const CHANNEL = {
  email: { label: "Email", icon: Mail, color: "#8c3add" },
  sms: { label: "SMS", icon: MessageSquare, color: "#1fa7a1" },
  whatsapp: { label: "WhatsApp", icon: Phone, color: "#22c55e" },
};


const DIFFICULTIES = ["all", "easy", "medium", "hard"];

const emptyForm = {
  name: "",
  subject: "",
  body: "",
  html_body: "",
  category: "",
  difficulty: "medium",
  simulation_type: "email",
  sender_id: "",
  has_report_button: false,
  embed_qr: false,
  include_tracking_pixel: true,
  include_tracking_link: true,
  tags: "",
};
const SEARCH_KEY = "search";

// Interakt (WhatsApp) approval status — sirf whatsapp templates par lagu.
// Backend response me status yahan hota hai: template.meta.interakt.approval_status
const getApprovalStatus = (t) => {
  if (!t || t.simulation_type !== "whatsapp") return null;
  const raw = t.meta?.interakt?.approval_status ?? t.approval_status;
  return typeof raw === "string" ? raw.toLowerCase() : null;
};

const APPROVAL_BADGE = {
  pending: { label: "Pending", classes: "bg-amber-100 text-amber-700 hover:bg-amber-100 cursor-default" },
  approved: { label: "Approved", classes: "bg-green-100 text-green-700 hover:bg-green-100 cursor-default" },
};

// Backend error se hamesha STRING nikalta hai (toast me object dene se React crash hota hai).
// Handles: string / {message, error, request_id} / FastAPI 422 array / network error
const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error ?? data;

  if (typeof detail === "string") return detail;

  // FastAPI 422: [{ loc: [...], msg: "..." }]
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
        return field ? `${field}: ${d.msg}` : d.msg || JSON.stringify(d);
      })
      .join(", ");
  }

  // Object jaise { message, error, request_id }
  if (detail && typeof detail === "object") {
    const msg = detail.message || detail.error || detail.detail;
    if (typeof msg === "string") return msg;
    return JSON.stringify(detail);
  }

  return err?.message || fallback;
};

const TemplateLibrary = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const isFirstRender = useRef(true);
  const [channel, setChannel] = useState("all");
  const [category, setCategory] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [cloning, setCloning] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Create / Edit
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Category combobox (departments)
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  const categoryScrollRef = useRef(null);
  const scrollCategories = (dir) => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({ left: dir * 150, behavior: "smooth" });
    }
  };

  // Media-library pickers (images / attachments) — selection lives in local
  // state and is only persisted when the template is saved.
  const [mediaImages, setMediaImages] = useState([]);
  const [mediaImagesLoading, setMediaImagesLoading] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const [mediaAttachments, setMediaAttachments] = useState([]);
  const [mediaAttachmentsLoading, setMediaAttachmentsLoading] = useState(false);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => fetchLib(), q === "" ? 0 : 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, channel, category, difficulty]);

  useEffect(() => { fetchDepartments(); }, []);

  // Load Media library lists whenever the dialog is open, editing an existing
  // email template (images / attachments only apply to saved email templates).
  useEffect(() => {
    if (isDialogOpen && editingId && formData.simulation_type === "email") {
      fetchMediaImages();
      fetchMediaAttachments();
    }
  }, [isDialogOpen, editingId, formData.simulation_type]);

  const fetchMediaImages = async () => {
    setMediaImagesLoading(true);
    try {
      const res = await getMedia("image");
      const raw = res.data?.data ?? [];
      setMediaImages(Array.isArray(raw) ? raw : []);
    } catch (error) {
      toast.error("Failed to load images from Media");
    }
    setMediaImagesLoading(false);
  };

  const fetchMediaAttachments = async () => {
    setMediaAttachmentsLoading(true);
    try {
      const res = await getMedia("attachment");
      const raw = res.data?.data ?? [];
      setMediaAttachments(Array.isArray(raw) ? raw : []);
    } catch (error) {
      toast.error("Failed to load attachments from Media");
    }
    setMediaAttachmentsLoading(false);
  };

  const fetchDepartments = async () => {
    setDepartmentsLoading(true);
    try {
      const response = await getDepartments();
      setDepartments(
        Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || []
      );
    } catch (error) {
      toast.error("Failed to load departments");
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const fetchLib = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params[SEARCH_KEY] = q;
      if (channel !== "all") params.channel = channel;
      if (category !== "all") params.category = category;
      if (difficulty !== "all") params.difficulty = difficulty;
      const res = await getTemplateLibrary(params);
      const list = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.items || res?.data?.results || [];
      setItems(list);
      setTotal(res?.data?.count ?? list.length);
    } catch { toast.error("Failed to load library"); }
    setLoading(false);
  };

  const clone = async (id) => {
    const target = items.find((x) => x.id === id);
    if (getApprovalStatus(target) === "pending") {
      toast.error("Template approval pending hai — approve hone ke baad clone kar sakte ho");
      return;
    }
    setCloning(id);
    try {
      await cloneTemplate(id);
      toast.success("Cloned to your library — check Email Templates");
    } catch { toast.error("Clone failed"); }
    setCloning(null);
  };

  // Interakt (WhatsApp) templates sync
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncInteraktTemplates();
      const msg = res?.data?.message;
      toast.success(typeof msg === "string" ? msg : "Templates synced successfully");
      fetchLib(); // sync ke baad list refresh
    } catch (error) {
      toast.error(getErrorMessage(error, "Sync failed"));
    }
    setSyncing(false);
  };

  const badgeClasses = (t) => {
    if (t === "hard") return "bg-red-100 text-red-700";
    if (t === "medium") return "bg-amber-100 text-amber-700";
    return "bg-green-100 text-green-700";
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setSelectedImageIds([]);
    setSelectedAttachmentIds([]);
    setIsDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setFormData({
      name: t.name || "",
      subject: t.subject || "",
      body: t.body || "",
      html_body: t.html_body || "",
      category: t.category || "",
      difficulty: t.difficulty || "medium",
      simulation_type: t.simulation_type || "email",
      sender_id: t.sender_id || "",
      has_report_button: !!t.has_report_button,
      embed_qr: !!t.embed_qr,
      include_tracking_pixel: t.include_tracking_pixel !== false,
      include_tracking_link: t.include_tracking_link !== false,
      tags: Array.isArray(t.tags) ? t.tags.join(", ") : (t.tags || ""),
    });
    // Pre-fill from the template's already-attached media so both the
    // "currently attached" list and the picker start in sync. Removing an
    // item just drops its id from these arrays — no API call until Save.
    const existingMedia = t.media_data || [];
    setSelectedImageIds(existingMedia.filter((m) => m.media_type === "image").map((m) => m.id));
    setSelectedAttachmentIds(existingMedia.filter((m) => m.media_type === "attachment").map((m) => m.id));
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      tags: formData.tags
        ? formData.tags.split(",").map((tag) => tag.trim()).filter(Boolean)
        : [],
    };
    // SMS / WhatsApp don't need subject / html_body
    if (payload.simulation_type !== "email") {
      payload.html_body = "";
    }
    // Selected Media images/attachments go straight into the same payload —
    // backend attaches them itself when saving the template.
    if (payload.simulation_type === "email") {
      payload.media_ids = [...selectedImageIds, ...selectedAttachmentIds];
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateTemplateLibrary(editingId, payload);
        toast.success("Template updated");
      } else {
        await createTemplateLibrary(payload);
        toast.success("Template created");
      }
      setIsDialogOpen(false);
      setEditingId(null);
      setFormData(emptyForm);
      setSelectedImageIds([]);
      setSelectedAttachmentIds([]);
      fetchLib();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save template"));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteTemplateLibrary(deleteId);
      toast.success("Template deleted");
      setDeleteId(null);
      fetchLib();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete template"));
    }
    setDeleting(false);
  };

  // --- Media-library selection (images) ---
  const toggleImageSelection = (id) => {
    setSelectedImageIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // --- Media-library selection (attachments) ---
  const toggleAttachmentSelection = (id) => {
    setSelectedAttachmentIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const simType = formData.simulation_type;

  // Backend returns attached media in a single `media_data` array (mixed
  // images + attachments), distinguished by `media_type`. Only show items
  // still present in the local selection state — removing one just drops
  // its id from selectedImageIds/selectedAttachmentIds, no API call until Save.
  const editingTemplate = editingId ? items.find((t) => t.id === editingId) : null;
  const mediaDataList = editingTemplate?.media_data || [];
  const cidImages = mediaDataList.filter(
    (m) => m.media_type === "image" && selectedImageIds.includes(m.id)
  );
  const attachments = mediaDataList.filter(
    (m) => m.media_type === "attachment" && selectedAttachmentIds.includes(m.id)
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6 animate-fadeIn" data-testid="template-library-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1f1f23] flex items-center gap-2">
              <Library size={24} className="text-[#8c3add]" /> Template Library
            </h1>
            <p className="text-gray-500 text-sm mt-1">{total} curated phishing templates across Email, SMS, and WhatsApp</p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#8c3add] hover:bg-[#702eb0] text-white gap-2"
            data-testid="create-library-template-btn"
          >
            <Plus size={18} />
            New Template
          </Button>
        </div>

        {/* Filters */}
        <Card className="border border-gray-100 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / tag " className="pl-9" data-testid="library-search" />
              </div>
            </div>
            {/* Outer row: left = scrollable filters, right = fixed Sync button */}
            <div className="flex items-center gap-2">
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-1 min-w-0 flex-1">
              {/* Channel filter */}
              <Tabs value={channel} onValueChange={setChannel} className="shrink-0">
                <TabsList>
                  <TabsTrigger value="all" data-testid="filter-channel-all">All</TabsTrigger>
                  <TabsTrigger value="email">Email</TabsTrigger>
                  <TabsTrigger value="sms">SMS</TabsTrigger>
                  <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="w-px h-6 bg-gray-200 shrink-0" />

              {/* Category / Department filter — scrollable pills */}
              <div className="flex items-center gap-1 shrink-0 min-w-0">
                <Button
                  size="sm"
                  variant={category === "all" ? "default" : "outline"}
                  onClick={() => setCategory("all")}
                  className={`shrink-0 rounded-full ${category === "all" ? "bg-[#8c3add] hover:bg-[#702eb0]" : ""}`}
                >
                  All
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 rounded-full"
                  onClick={() => scrollCategories(-1)}
                >
                  <ChevronLeft size={16} />
                </Button>

                <div
                  ref={categoryScrollRef}
                  className="flex items-center gap-2 overflow-x-auto scroll-smooth max-w-[380px]"
                  style={{ scrollbarWidth: "none" }}
                >
                  {departmentsLoading ? (
                    <div className="flex items-center justify-center py-2 px-4">
                      <Loader2 size={16} className="animate-spin text-[#8c3add]" />
                    </div>
                  ) : (
                    departments.map((dept) => (
                      <Button
                        key={dept.id}
                        size="sm"
                        variant={category === dept.name ? "default" : "outline"}
                        onClick={() => setCategory(dept.name)}
                        className={`shrink-0 rounded-full whitespace-nowrap ${category === dept.name ? "bg-[#8c3add] hover:bg-[#702eb0]" : ""}`}
                      >
                        {dept.name}
                      </Button>
                    ))
                  )}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 rounded-full"
                  onClick={() => scrollCategories(1)}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>

              <div className="w-px h-6 bg-gray-200 shrink-0" />

              {/* Difficulty filter */}
              <div className="flex items-center gap-1 shrink-0">
                {DIFFICULTIES.map((d) => (
                  <Button
                    key={d}
                    size="sm"
                    variant={difficulty === d ? "default" : "outline"}
                    onClick={() => setDifficulty(d)}
                    className={`rounded-full ${difficulty === d ? "bg-[#1fa7a1] hover:bg-[#178a85]" : ""}`}
                  >
                    {d === "all" ? "All Levels" : d}
                  </Button>
                ))}
              </div>

            </div>

            {/* Sync button — right side par fixed; sirf All ya WhatsApp channel par dikhega */}
            {(channel === "all" || channel === "whatsapp") && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
                className="shrink-0 ml-auto rounded-full gap-1 border-[#8c3add] text-[#8c3add] bg-[#8c3add]/15 hover:bg-[#8c3add]/25"
                data-testid="sync-interakt-btn"
              >
                <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing..." : "Sync"}
              </Button>
            )}
            </div>
          </CardContent>
        </Card>


        {loading ? (
          <div className="w-full flex items-center justify-center py-24" data-testid="library-loading">
            <CulsightPageLoader />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch" data-testid="library-grid">
            {items.length === 0 ? (
              <div className="col-span-full py-16 text-center text-gray-500 border border-dashed rounded-xl">
                <Library size={32} className="mx-auto mb-2 text-gray-300" />
                No templates match your filters.
              </div>
            ) : items.map((t) => {
              const meta = CHANNEL[t.simulation_type] || CHANNEL.email;
              const Icon = meta.icon;
              const approval = getApprovalStatus(t);
              const isPending = approval === "pending";
              return (
                <Card
                  key={t.id}
                  className="h-full border border-gray-100 shadow-sm hover:shadow-md transition-all"
                  data-testid={`lib-card-${t.id}`}
                >
                  <CardContent className="p-5 h-full flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${meta.color}15` }}>
                        <Icon size={18} style={{ color: meta.color }} />
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <Badge className={badgeClasses(t.difficulty)}>{t.difficulty}</Badge>
                        <Badge variant="outline" className="text-xs">{t.category}</Badge>
                      </div>
                    </div>
                    <h3 className="font-semibold text-[#1f1f23] mt-3 line-clamp-1">{t.name}</h3>
                    {t.subject && <p className="text-xs text-gray-500 mt-1 line-clamp-1">Subject: {t.subject}</p>}
                    <p className="text-sm text-gray-500 mt-2 line-clamp-3">{t.body}</p>
                    {t.tags && t.tags.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-3">
                        {t.tags.slice(0, 4).map((tag) => (
                          <span key={tag} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{tag}</span>
                        ))}
                      </div>
                    )}

                    {/* Footer — always pinned to the bottom of the card */}
                    <div className="mt-auto pt-4">
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-400">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}
                        </span>
                        <div className="flex gap-1 items-center">
                          {/* Interakt approval status — clone icon ke bagal me */}
                          {approval && APPROVAL_BADGE[approval] && (
                            <Badge className={`text-[10px] px-2 py-0.5 ${APPROVAL_BADGE[approval].classes}`}>
                              {APPROVAL_BADGE[approval].label}
                            </Badge>
                          )}

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={isPending ? "cursor-not-allowed" : undefined}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`p-2 ${isPending ? "opacity-40 pointer-events-none" : "hover:bg-[#8c3add]/10"}`}
                                  onClick={() => !isPending && clone(t.id)}
                                  disabled={cloning === t.id || isPending}
                                  data-testid={`clone-${t.id}`}
                                >
                                  {cloning === t.id ? (
                                    <Loader2 size={16} className="text-[#8c3add] animate-spin" />
                                  ) : isPending ? (
                                    <span className="relative inline-flex items-center justify-center">
                                      <Copy size={16} className="text-[#8c3add]" />
                                      <span className="absolute w-[18px] h-[2px] bg-red-500 rotate-45 rounded-full" />
                                    </span>
                                  ) : (
                                    <Copy size={16} className="text-[#8c3add]" />
                                  )}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isPending
                                ? "Approval pending"
                                : "Clone to Email Templates"}
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 hover:bg-[#1fa7a1]/10"
                                onClick={() => setPreview(t)}
                              >
                                <Eye size={16} className="text-[#1fa7a1]" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Preview</TooltipContent>
                          </Tooltip>

                          {t.simulation_type !== "whatsapp" && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="p-2 hover:bg-[#1fa7a1]/10"
                                    onClick={() => openEdit(t)}
                                    data-testid={`edit-lib-${t.id}`}
                                  >
                                    <Edit size={16} className="text-[#1fa7a1]" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="p-2 hover:bg-red-50"
                                    onClick={() => setDeleteId(t.id)}
                                    data-testid={`delete-lib-${t.id}`}
                                  >
                                    <Trash2 size={16} className="text-red-500" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Preview dialog */}
        <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{preview?.name}</DialogTitle></DialogHeader>
            {preview && (
              <div className="space-y-3">
                <div className="flex gap-2 flex-wrap">
                  <Badge>{preview.simulation_type}</Badge>
                  <Badge className={badgeClasses(preview.difficulty)}>{preview.difficulty}</Badge>
                  <Badge variant="outline">{preview.category}</Badge>
                  {APPROVAL_BADGE[getApprovalStatus(preview)] && (
                    <Badge className={APPROVAL_BADGE[getApprovalStatus(preview)].classes}>
                      {APPROVAL_BADGE[getApprovalStatus(preview)].label}
                    </Badge>
                  )}
                </div>
                {preview.subject && <div><p className="text-xs text-gray-500">Subject</p><p className="font-medium">{preview.subject}</p></div>}
                {preview.html_body ? (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">HTML Preview</p>
                    <div className="border rounded-lg overflow-hidden">
                      <iframe srcDoc={preview.html_body} className="w-full h-80" sandbox="allow-same-origin" title="preview" />
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded whitespace-pre-wrap text-sm">{preview.body}</div>
                )}
              </div>
            )}
            <DialogFooter>
              {preview?.simulation_type !== "whatsapp" && (
                <Button
                  variant="outline"
                  onClick={() => { const t = preview; setPreview(null); openEdit(t); }}
                  className="gap-1"
                >
                  <Edit size={14} /> Edit
                </Button>
              )}
              <Button
                className="bg-[#8c3add] hover:bg-[#702eb0]"
                disabled={getApprovalStatus(preview) === "pending"}
                onClick={() => { clone(preview.id); setPreview(null); }}
              >
                <Copy size={14} className="mr-1" /> Clone to My Templates
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create / Edit dialog */}
        <Dialog
          open={isDialogOpen}
          onOpenChange={(o) => {
            setIsDialogOpen(o);
            if (!o) {
              setEditingId(null);
              setFormData(emptyForm);
              setSelectedImageIds([]);
              setSelectedAttachmentIds([]);
            }
          }}
        >
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="library-template-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingId ? <Edit size={18} className="text-[#8c3add]" /> : <Plus size={18} className="text-[#8c3add]" />}
                {editingId ? "Edit Library Template" : "Create Library Template"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Password Reset Template"
                    required
                    data-testid="library-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Simulation Type</Label>
                  <Select
                    value={formData.simulation_type}
                    onValueChange={(value) => setFormData({ ...formData, simulation_type: value })}
                  >
                    <SelectTrigger data-testid="library-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      {/* <SelectItem value="whatsapp">WhatsApp</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={categoryOpen}
                        className="w-full justify-between font-normal"
                        data-testid="library-category-trigger"
                      >
                        {formData.category || "Select Category"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                      <Command>
                        <CommandInput placeholder="Search category..." data-testid="library-category-search" />
                        <CommandList>
                          {departmentsLoading ? (
                            <div className="py-6 text-center text-sm text-gray-500">Loading departments...</div>
                          ) : departments.length === 0 ? (
                            <div className="py-6 text-center text-sm text-gray-500">No departments found</div>
                          ) : (
                            <>
                              <CommandEmpty>No matching category</CommandEmpty>
                              <CommandGroup>
                                {departments.map((department) => (
                                  <CommandItem
                                    key={department.id}
                                    value={department.name}
                                    onSelect={(currentValue) => {
                                      setFormData({ ...formData, category: currentValue });
                                      setCategoryOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${formData.category === department.name ? "opacity-100" : "opacity-0"
                                        }`}
                                    />
                                    {department.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <Select
                    value={formData.difficulty}
                    onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
                  >
                    <SelectTrigger data-testid="library-difficulty-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {simType === "email" && (
                <div className="space-y-2">
                  <Label>Subject Line</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Urgent: Action Required"
                    required
                    data-testid="library-subject-input"
                  />
                </div>
              )}

              {(simType === "sms" || simType === "whatsapp") && (
                <div className="space-y-2">
                  <Label>{simType === "sms" ? "Sender ID (max 11 chars)" : "Sender Name"}</Label>
                  <Input
                    value={formData.sender_id}
                    onChange={(e) => setFormData({ ...formData, sender_id: e.target.value })}
                    placeholder={simType === "sms" ? "HRDEPT" : "HR Department"}
                    maxLength={simType === "sms" ? 11 : 50}
                    data-testid="library-sender-input"
                  />
                </div>
              )}

              {simType === "email" ? (
                <Tabs defaultValue="html" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="html" data-testid="library-html-tab">HTML Body</TabsTrigger>
                    <TabsTrigger value="plain" data-testid="library-plain-tab">Plain Text (fallback)</TabsTrigger>
                  </TabsList>
                  <TabsContent value="html" className="space-y-2">
                    <Label>HTML Content</Label>
                    <Textarea
                      value={formData.html_body}
                      onChange={(e) => setFormData({ ...formData, html_body: e.target.value })}
                      placeholder={'<html><body><img src="cid:logo" /> ... </body></html>'}
                      rows={12}
                      className="font-mono text-xs"
                      data-testid="library-html-input"
                    />
                  </TabsContent>
                  <TabsContent value="plain" className="space-y-2">
                    <Label>Plain Text</Label>
                    <Textarea
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      placeholder="Plain text version of the email..."
                      rows={8}
                      required
                      data-testid="library-body-input"
                    />
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="space-y-2">
                  <Label>{simType === "sms" ? "SMS Message (max 160 chars)" : "WhatsApp Message"}</Label>
                  <Textarea
                    value={formData.body}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    placeholder={simType === "sms" ? "Your verification code is..." : "Hello! Your account requires..."}
                    rows={6}
                    maxLength={simType === "sms" ? 160 : 1000}
                    required
                    data-testid="library-body-input"
                  />
                  <p className="text-xs text-gray-500">{formData.body.length} characters</p>
                </div>
              )}

              {/* Tracking */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm">Report Button</Label>
                  <Switch
                    checked={formData.has_report_button}
                    onCheckedChange={(v) => setFormData({ ...formData, has_report_button: v })}
                  />
                </div>
                {simType === "email" && (
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm">Tracking Pixel</Label>
                    <Switch
                      checked={formData.include_tracking_pixel}
                      onCheckedChange={(v) => setFormData({ ...formData, include_tracking_pixel: v })}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm">Tracking Link</Label>
                  <Switch
                    checked={formData.include_tracking_link}
                    onCheckedChange={(v) => setFormData({ ...formData, include_tracking_link: v })}
                  />
                </div>
                {simType === "email" && (
                  <div className="flex items-center justify-between gap-2" data-testid="library-qr-toggle-wrap">
                    <Label className="text-sm">Embed QR Code</Label>
                    <Switch
                      checked={formData.embed_qr}
                      onCheckedChange={(v) => setFormData({ ...formData, embed_qr: v })}
                      data-testid="library-qr-toggle"
                    />
                  </div>
                )}
              </div>
              {simType === "email" && formData.embed_qr && (
                <div className="p-3 bg-[#8c3add]/5 border border-[#8c3add]/20 rounded-lg text-xs text-gray-700">
                  💡 QR embedding is <strong>on</strong>. At send time, we replace the placeholder{" "}
                  <code className="bg-white px-1 rounded">{"{{qr_image}}"}</code>{" "}
                  in your HTML with a per-recipient tracked QR code. Insert{" "}
                  <code className="bg-white px-1 rounded">{'<img src="{{qr_image}}" width="180"/>'}</code>{" "}
                  anywhere in the HTML.
                </div>
              )}

              {/* Embedded CID Images — selected from Media library instead of local upload */}
              {simType === "email" && editingId && (
                <div className="border border-dashed border-gray-200 rounded-lg p-4 space-y-3" data-testid="library-cid-images-section">
                  <div className="flex items-center gap-2">
                    <ImageIcon size={16} className="text-[#8c3add]" />
                    <Label className="font-medium">Embedded CID Images</Label>
                  </div>

                  {/* Currently attached */}
                  {cidImages.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {cidImages.map((img) => (
                        <div key={img.id} className="border rounded-lg p-2 flex items-center gap-2 bg-white">
                          <img
                            src={img.url}
                            alt={img.filename}
                            className="w-10 h-10 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-mono truncate">{img.filename}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {img.size ? `${(img.size / 1024).toFixed(1)} KB` : ""}
                            </p>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="p-1 h-auto hover:bg-red-50"
                                onClick={() => toggleImageSelection(img.id)}
                              >
                                <X size={14} className="text-red-500" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove image</TooltipContent>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pick images from Media library — list only opens on "Select" click */}
                  <div className="flex items-center gap-2">
                    <Popover open={imagePickerOpen} onOpenChange={setImagePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          data-testid="library-select-images-btn"
                        >
                          <ImageIcon size={14} />
                          Select Images{selectedImageIds.length ? ` (${selectedImageIds.length})` : ""}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <p className="text-xs font-medium text-gray-500 mb-2">Images from Media</p>
                        {mediaImagesLoading ? (
                          <div className="py-4 flex justify-center">
                            <Loader2 size={20} className="animate-spin text-[#8c3add]" />
                          </div>
                        ) : mediaImages.length === 0 ? (
                          <div className="py-4 text-center text-sm text-gray-400">
                            No images in Media library yet — upload some in the Media page first.
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                            {mediaImages.map((m) => (
                              <label
                                key={m.id}
                                className={`flex items-center gap-2 border rounded-lg p-2 cursor-pointer transition-colors ${selectedImageIds.includes(m.id)
                                  ? "border-[#8c3add] bg-[#8c3add]/5"
                                  : "border-gray-200 bg-white hover:bg-gray-50"
                                  }`}
                              >
                                <Checkbox
                                  checked={selectedImageIds.includes(m.id)}
                                  onCheckedChange={() => toggleImageSelection(m.id)}
                                />
                                {m.url ? (
                                  <img src={m.url} alt={m.filename} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <ImageIcon size={14} className="text-gray-300" />
                                  </div>
                                )}
                                <span className="text-xs truncate">{m.filename}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          className="w-full mt-3 bg-[#8c3add] hover:bg-[#702eb0] text-white"
                          onClick={() => setImagePickerOpen(false)}
                          disabled={selectedImageIds.length === 0}
                        >
                          Done{selectedImageIds.length ? ` (${selectedImageIds.length} selected)` : ""}
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-gray-400 italic">Selected images will attach when you click Save Changes below.</p>
                </div>
              )}
              {simType === "email" && !editingId && (
                <p className="text-xs text-gray-500 italic">Save the template first to attach images from Media.</p>
              )}

              {/* Decoy Attachments — selected from Media library instead of local upload */}
              {simType === "email" && editingId && (
                <div className="border border-dashed border-gray-200 rounded-lg p-4 space-y-3" data-testid="library-attachments-section">
                  <div className="flex items-center gap-2">
                    <Paperclip size={16} className="text-[#1fa7a1]" />
                    <Label className="font-medium">Decoy Attachments</Label>
                    <span className="text-xs text-gray-400">from Media · downloads are tracked</span>
                  </div>

                  {/* Currently attached */}
                  {attachments.length > 0 && (
                    <div className="space-y-1.5">
                      {attachments.map((att) => (
                        <div key={att.id} className="flex items-center gap-2 p-2 border rounded-lg bg-white" data-testid={`library-att-row-${att.id}`}>
                          <Paperclip size={14} className="text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{att.filename}</p>
                            <p className="text-[10px] text-gray-400">
                              {att.content_type} · {(att.size / 1024).toFixed(1)}KB
                            </p>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="p-1 h-auto hover:bg-red-50"
                                onClick={() => toggleAttachmentSelection(att.id)}
                              >
                                <X size={14} className="text-red-500" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove attachment</TooltipContent>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pick attachments from Media library — list only opens on "Select" click */}
                  <div className="flex items-center gap-2">
                    <Popover open={attachmentPickerOpen} onOpenChange={setAttachmentPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          data-testid="library-select-attachments-btn"
                        >
                          <Paperclip size={14} />
                          Select Attachments{selectedAttachmentIds.length ? ` (${selectedAttachmentIds.length})` : ""}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <p className="text-xs font-medium text-gray-500 mb-2">Attachments from Media</p>
                        {mediaAttachmentsLoading ? (
                          <div className="py-4 flex justify-center">
                            <Loader2 size={20} className="animate-spin text-[#1fa7a1]" />
                          </div>
                        ) : mediaAttachments.length === 0 ? (
                          <div className="py-4 text-center text-sm text-gray-400">
                            No attachments in Media library yet — upload some in the Media page first.
                          </div>
                        ) : (
                          <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {mediaAttachments.map((m) => (
                              <label
                                key={m.id}
                                className={`flex items-center gap-2 border rounded-lg p-2 cursor-pointer transition-colors ${selectedAttachmentIds.includes(m.id)
                                  ? "border-[#1fa7a1] bg-[#1fa7a1]/5"
                                  : "border-gray-200 bg-white hover:bg-gray-50"
                                  }`}
                              >
                                <Checkbox
                                  checked={selectedAttachmentIds.includes(m.id)}
                                  onCheckedChange={() => toggleAttachmentSelection(m.id)}
                                />
                                <Paperclip size={14} className="text-gray-400 flex-shrink-0" />
                                <span className="text-xs flex-1 truncate">{m.filename}</span>
                                {m.size && (
                                  <span className="text-[10px] text-gray-400 flex-shrink-0">
                                    {(m.size / 1024).toFixed(1)}KB
                                  </span>
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          className="w-full mt-3 bg-[#1fa7a1] hover:bg-[#178a85] text-white"
                          onClick={() => setAttachmentPickerOpen(false)}
                          disabled={selectedAttachmentIds.length === 0}
                        >
                          Done{selectedAttachmentIds.length ? ` (${selectedAttachmentIds.length} selected)` : ""}
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-gray-400 italic">Selected attachments will attach when you click Save Changes below.</p>
                </div>
              )}
              {simType === "email" && !editingId && (
                <p className="text-xs text-gray-500 italic">Save the template first to attach files from Media.</p>
              )}

              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="urgent, finance, invoice"
                  data-testid="library-tags-input"
                />
              </div>

              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#8c3add] hover:bg-[#702eb0]" disabled={saving} data-testid="library-submit-btn">
                  {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  {editingId ? "Save Changes" : "Create Template"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 size={18} className="text-red-500" />
                Delete Library Template
              </DialogTitle>
            </DialogHeader>
            <div className="py-3">
              <p className="text-sm text-gray-600">Are you sure you want to delete this template?</p>
              <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeleteId(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deleting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Trash2 size={16} className="mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default TemplateLibrary;